import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { z } from "zod";
import { Flame, Star, ArrowUpRight, Lock } from "lucide-react";
import { VanFastIcon, ShieldKeyholeIcon, BoxAltIcon, StarFilledIcon } from "@/components/icons/PerkIcons";
import heroBirriaAsset from "@/assets/hero-birria.jpg.asset.json";
const heroBirria = heroBirriaAsset.url;
import quesabirria from "@/assets/quesabirria.jpg";
import streetNightAsset from "@/assets/street-night.jpg.asset.json";
const streetNight = streetNightAsset.url;
import community from "@/assets/community.jpg";
import { useEntry, useEntries, useField, type MergedEntry } from "@/lib/content";

const SITE_URL = "https://tacoya.snapinsolutions.com";

const SHOP_PRODUCTS = [
  { name: "Chili Crunch No.1", price: "12", desc: "Toasted chiles, garlic, sesame. Drizzle on everything." },
  { name: "Salsa Verde Cruda", price: "10", desc: "Tomatillo, jalapeño, cilantro. Bright and loud." },
  { name: "Yellow Heat", price: "11", desc: "Habanero, mustard, lime. The Tacoya yellow." },
  { name: "Smoked Morita", price: "12", desc: "Slow-smoked morita chiles. Deep, almost sweet." },
  { name: "Tacoya Seasoning", price: "9", desc: "House blend used behind the line." },
  { name: "Birria Spice Mix", price: "14", desc: "Make our birria at home. 4-portion pack." },
  { name: "Logo Tee — Black", price: "39", desc: "Heavy 240gsm cotton. Front mark, back wordmark." },
  { name: "Hietalahti Hoodie", price: "79", desc: "Tribute to where it started. 400gsm fleece." },
];

export const Route = createFileRoute("/shop")({
  head: () => ({
    meta: [
      { title: "Shop — Tacoya Sauces, Spice & Merch Drop 001" },
      { name: "description", content: "Drop 001: Tacoya sauces, seasonings and apparel. Limited first run. Sign up for early access before the public drop." },
      { property: "og:title", content: "Tacoya Shop — Drop 001" },
      { property: "og:description", content: "Sauces. Spice. Streetwear. The first Tacoya drop is loading." },
      { property: "og:url", content: `${SITE_URL}/shop` },
      { property: "og:image", content: heroBirria },
      { name: "twitter:image", content: heroBirria },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}/shop` }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: "Tacoya Shop — Drop 001",
          itemListElement: SHOP_PRODUCTS.map((p, i) => ({
            "@type": "ListItem",
            position: i + 1,
            item: {
              "@type": "Product",
              name: p.name,
              description: p.desc,
              brand: { "@type": "Brand", name: "Tacoya" },
              offers: {
                "@type": "Offer",
                price: p.price,
                priceCurrency: "EUR",
                availability: "https://schema.org/PreOrder",
                url: `${SITE_URL}/shop`,
              },
            },
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
            { "@type": "ListItem", position: 2, name: "Shop", item: `${SITE_URL}/shop` },
          ],
        }),
      },
    ],
  }),
  component: ShopPage,
});

const emailSchema = z.string().trim().email("Enter a valid email").max(255);


function ShopPage() {
  return (
    <div>
      <Hero />
      <Marquee />
      <Featured />
      <FilterableGrid />
      <Lookbook />
      <Perks />
      <FAQ />
      <Notify />
    </div>
  );
}

/* ---------- HERO ---------- */
function Hero() {
  const hero = useEntry("shop", "hero");
  const f = useField();
  const countdownAt = typeof hero?.data.countdown_at === "string" ? hero.data.countdown_at : "2026-06-05T18:00:00Z";
  const target = useCountdown(countdownAt);
  return (
    <section className="relative overflow-hidden border-b border-border">
      {/* Background image */}
      <div
        className="absolute inset-0 opacity-40"
        style={{ backgroundImage: `url(${hero?.image ?? streetNight})`, backgroundSize: "cover", backgroundPosition: "center" }}
      />
      {/* Gradients & atmosphere */}
      <div className="absolute inset-0 bg-gradient-to-b from-charcoal/80 via-charcoal/85 to-background" />
      <div className="absolute -top-32 -right-20 size-[520px] rounded-full bg-yellow/15 blur-[120px] pointer-events-none" />
      <div className="absolute -bottom-40 -left-20 size-[520px] rounded-full bg-chili/20 blur-[140px] pointer-events-none" />
      {/* Subtle grid */}
      <div className="absolute inset-0 opacity-[0.02] pointer-events-none" style={{ backgroundImage: "linear-gradient(to right, white 1px, transparent 1px), linear-gradient(to bottom, white 1px, transparent 1px)", backgroundSize: "80px 80px" }} />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-5 md:px-8 pt-14 pb-16 md:pt-32 md:pb-36">
        {/* Status pill */}
        <div className="inline-flex flex-wrap items-center gap-x-2 gap-y-1 sm:gap-2.5 rounded-full border border-cream/15 bg-cream/[0.04] backdrop-blur-md px-3 py-1.5 sm:px-4 sm:py-2 text-[9px] sm:text-[11px] uppercase tracking-[0.22em] sm:tracking-[0.3em] text-cream/80 mb-6 md:mb-10 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.08)]">
          <span className="relative flex size-2">
            <span className="absolute inset-0 rounded-full bg-chili animate-ping opacity-75" />
            <span className="relative size-2 rounded-full bg-chili" />
          </span>
          <span>{f(hero, "status_a", "Drop 001 · Loading")}</span>
          <span className="text-cream/30 hidden sm:inline">/</span>
          <span className="text-yellow">{f(hero, "status_b", "Early access open")}</span>
        </div>

        <div className="grid lg:grid-cols-12 gap-8 lg:gap-14 items-end">
          {/* Headline + copy */}
          <div className="lg:col-span-7">
            <h1 className="font-display text-[22vw] sm:text-[14vw] md:text-[10rem] lg:text-[11rem] leading-[1.05] md:leading-[0.95] text-cream tracking-tight">
              <span className="block">{f(hero, "headline_a", "TASTE")}</span>
              <span className="block text-yellow drop-shadow-[0_8px_30px_rgba(247,196,42,0.35)] mt-2 md:mt-3">{f(hero, "headline_b", "TACOYA")}</span>
              <span className="block text-stroke mt-2 md:mt-3">{f(hero, "headline_c", "AT HOME.")}</span>
            </h1>

            <p className="mt-6 md:mt-8 max-w-xl text-cream/70 text-[15px] sm:text-lg leading-relaxed">
              {f(hero, "copy", "The first Tacoya drop. Sauces bottled from our kitchen, spice blends used behind our line, and apparel built like the food — heavy, honest, made to last. Limited first run.")}
            </p>

          </div>

          {/* Countdown card */}
          <div className="lg:col-span-5">
            <div className="relative rounded-3xl border border-cream/12 bg-gradient-to-br from-cream/[0.07] via-cream/[0.03] to-transparent backdrop-blur-2xl p-6 sm:p-8 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.1),0_30px_60px_-30px_rgba(0,0,0,0.9)] overflow-hidden">
              {/* Top sheen */}
              <span className="pointer-events-none absolute inset-x-8 top-0 h-px bg-gradient-to-r from-transparent via-cream/40 to-transparent" />
              {/* Corner glow */}
              <span className="pointer-events-none absolute -top-20 -right-20 size-48 rounded-full bg-yellow/15 blur-3xl" />

              <div className="flex items-center justify-between mb-5">
                <div className="text-[10px] uppercase tracking-[0.3em] text-cream/60">Drops in</div>
                <div className="font-mono text-[10px] text-yellow/90">T-MINUS</div>
              </div>

              <div className="grid grid-cols-4 gap-2 sm:gap-3">
                {target.map((t) => (
                  <div
                    key={t.label}
                    className="relative overflow-hidden rounded-xl border border-cream/20 backdrop-blur-2xl px-2 py-4 sm:px-3 sm:py-5 text-center"
                    style={{
                      background:
                        "linear-gradient(140deg, color-mix(in oklab, white 10%, transparent) 0%, color-mix(in oklab, white 2%, transparent) 55%, color-mix(in oklab, white 6%, transparent) 100%)",
                      boxShadow:
                        "inset 0 1px 0 0 rgba(255,255,255,0.22), inset 0 -1px 0 0 rgba(255,255,255,0.05)",
                    }}
                  >
                    {/* Top sheen */}
                    <span aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-cream/70 to-transparent" />
                    {/* Diagonal shine */}
                    <span
                      aria-hidden
                      className="pointer-events-none absolute -top-1/2 left-0 w-2/3 h-[200%] rotate-[18deg] opacity-50"
                      style={{
                        background:
                          "linear-gradient(90deg, transparent, rgba(255,255,255,0.10), transparent)",
                      }}
                    />
                    <div className="relative font-sans font-semibold text-3xl sm:text-4xl md:text-5xl text-cream tabular-nums leading-none tracking-tight">
                      {t.value}
                    </div>
                    <div className="relative font-sans text-[10px] uppercase tracking-[0.2em] text-cream/60 mt-2 font-medium">
                      {t.label}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6 pt-5 border-t border-cream/10 flex items-center justify-between gap-3">
                <div>
                  <div className="font-display text-2xl text-yellow leading-none">{f(hero, "launch_label", "June 5 · 21:00")}</div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-cream/45 mt-1.5">{f(hero, "launch_sub", "Helsinki time · 1500 units")}</div>
                </div>
                <span className="hidden sm:inline-flex items-center justify-center size-11 rounded-full bg-yellow text-charcoal shadow-[0_10px_30px_-8px_color-mix(in_oklab,var(--tacoya-yellow)_60%,transparent)]">
                  <ArrowUpRight className="size-5" />
                </span>
              </div>
            </div>

            {/* Trust row */}
            <div className="mt-4 flex items-center gap-3 text-[10px] uppercase tracking-[0.28em] text-cream/45">
              <div className="flex -space-x-1.5">
                {[1,2,3,4,5].map((i) => (
                  <Star key={i} size={12} className="fill-yellow text-yellow drop-shadow" />
                ))}
              </div>
              <span>{f(hero, "trust_text", "1,200+ on the waitlist")}</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}



function useCountdown(targetIso: string) {
  // Fixed launch date so SSR + client agree on the initial render
  const target = new Date(targetIso).getTime();
  const [now, setNow] = useState<number | null>(null);
  useEffect(() => {
    setNow(Date.now());
    const i = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(i);
  }, []);
  const diff = now === null ? Math.max(0, target - Date.parse("2026-05-22T00:00:00Z")) : Math.max(0, target - now);
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  const pad = (n: number) => n.toString().padStart(2, "0");
  return [
    { label: "Days", value: pad(d) },
    { label: "Hours", value: pad(h) },
    { label: "Minutes", value: pad(m) },
    { label: "Seconds", value: pad(s) },
  ];
}



/* ---------- MARQUEE ---------- */
function Marquee() {
  const items = ["DROP 001", "★", "BOTTLED IN HELSINKI", "★", "LIMITED RUN", "★", "FREE SHIPPING > 60€", "★", "SHIPS TO EU", "★"];
  return (
    <div className="bg-yellow text-charcoal overflow-hidden border-b border-charcoal/20">
      <div className="flex gap-10 py-3 marquee whitespace-nowrap font-display text-2xl">
        {[...items, ...items, ...items].map((t, i) => (
          <span key={i}>{t}</span>
        ))}
      </div>
    </div>
  );
}

/* ---------- FEATURED ---------- */
function Featured() {
  const featured = useEntry("shop", "featured");
  const f = useField();
  return (
    <section className="mx-auto max-w-7xl px-4 sm:px-5 md:px-8 py-14 md:py-24">
      <SectionHead kicker={f(featured, "kicker", "Drop hero")} title={f(featured, "title", "CHILI CRUNCH No.1")} right={f(featured, "right", "The bottle that started the queue.")} />
      <div className="mt-10 grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-7 relative aspect-[4/3] lg:aspect-auto overflow-hidden border border-border group">
          <img src={featured?.image ?? quesabirria} alt={f(featured, "alt", "Chili Crunch No.1")} className="absolute inset-0 size-full object-cover group-hover:scale-105 transition-transform duration-700" />
          <div className="absolute inset-0 bg-gradient-to-tr from-charcoal/80 via-transparent to-transparent" />
          <div className="absolute top-5 left-5 flex gap-2">
            <Badge>Hero</Badge>
            <Badge variant="chili">Heat 3/3</Badge>
          </div>
          <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between">
            <div className="font-display text-4xl sm:text-5xl md:text-7xl text-cream leading-none">No.1</div>
            <div className="font-mono text-xs text-cream/80">200ML · GLASS</div>
          </div>
        </div>
        <div className="lg:col-span-5 bg-card border border-border p-5 sm:p-8 md:p-10 flex flex-col justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-yellow mb-3">Sauce · Limited</div>
            <h3 className="font-display text-2xl sm:text-3xl md:text-4xl text-cream leading-tight">Toasted chiles. Garlic. Sesame. Slow oil.</h3>
            <p className="mt-4 text-sm sm:text-base text-cream/70 leading-relaxed">
              Built behind the line over two years. Crunchy, smoky, balanced — not just hot.
              Spoon it on tacos, eggs, ramen, anything. The bottle people kept asking us to sell.
            </p>
            <ul className="mt-5 space-y-2 text-[13px] sm:text-sm text-cream/80">
              {["Single-origin morita & guajillo", "Cold-pressed sunflower oil", "Bottled in Helsinki, by hand"].map((f) => (
                <li key={f} className="flex gap-3 items-center">
                  <span className="size-1.5 bg-yellow shrink-0" /> {f}
                </li>
              ))}
            </ul>
          </div>
          <div className="mt-6 flex items-center justify-between gap-3 border-t border-border pt-5">
            <div>
              <div className="font-display text-3xl sm:text-4xl text-yellow">12 €</div>
              <div className="text-[10px] uppercase tracking-widest text-cream/50 mt-1">Drop price · 1500 bottles</div>
            </div>
            <button disabled className="bg-cream/10 text-cream/50 cursor-not-allowed px-4 py-3 sm:px-6 sm:py-4 text-[10px] sm:text-xs uppercase tracking-widest font-bold inline-flex items-center gap-2 shrink-0">
              <Lock className="size-3.5" /> Drops Soon
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- GRID ---------- */
const cats = ["All", "Sauce", "Spice", "Apparel"] as const;
function FilterableGrid() {
  const [cat, setCat] = useState<(typeof cats)[number]>("All");
  const products = useEntries("shop", "products");
  const catOf = (p: MergedEntry) => (typeof p.data.cat === "string" ? p.data.cat : "");
  const filtered = cat === "All" ? products : products.filter((p) => catOf(p) === cat);
  return (
    <section className="mx-auto max-w-7xl px-4 sm:px-5 md:px-8 py-14 md:py-24 border-t border-border">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
        <SectionHead kicker="The drop" title="ALL PIECES" />
        <div className="flex gap-2 flex-wrap">
          {cats.map((c) => (
            <button
              key={c}
              onClick={() => setCat(c)}
              className={`px-4 py-2 text-xs uppercase tracking-widest border transition-colors ${
                cat === c
                  ? "bg-cream text-charcoal border-cream"
                  : "border-border text-cream/70 hover:border-yellow hover:text-cream"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {filtered.map((p) => (
          <ProductCard key={p.key} p={p} />
        ))}
      </div>
    </section>
  );
}

function ProductCard({ p }: { p: MergedEntry }) {
  const f = useField();
  const cat = typeof p.data.cat === "string" ? p.data.cat : "";
  const color = typeof p.data.color === "string" ? p.data.color : "var(--charcoal)";
  const heat = typeof p.data.heat === "number" ? p.data.heat : 0;
  const price = typeof p.data.price === "string" ? p.data.price : "";
  const name = f(p, "name");
  const tag = f(p, "tag");
  const desc = f(p, "desc");
  return (
    <article className="group relative rounded-2xl overflow-hidden bg-gradient-to-b from-card to-charcoal border border-cream/10 hover:border-yellow/50 transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_24px_60px_-24px_color-mix(in_oklab,var(--yellow)_50%,transparent)] flex flex-col">
      {/* shine sweep on hover */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0 z-20 -translate-x-full bg-gradient-to-r from-transparent via-yellow/10 to-transparent group-hover:translate-x-full transition-transform duration-1000"
      />

      <div className="relative aspect-[4/5] overflow-hidden">
        <div
          className="absolute inset-0"
          style={{ background: `radial-gradient(circle at 30% 20%, color-mix(in oklab, ${color} 70%, black) 0%, color-mix(in oklab, ${color} 30%, black) 60%, #0a0a0a 100%)` }}
        />
        {p.image && (
          <img
            src={p.image}
            alt={name}
            loading="lazy"
            width={768}
            height={960}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-charcoal via-charcoal/30 to-transparent" />

        {/* top row */}
        <div className="absolute inset-x-2.5 top-2.5 sm:inset-x-3 sm:top-3 z-10 flex items-start justify-between gap-2">
          <div className="flex flex-wrap gap-1 min-w-0">
            {cat && <Badge>{cat}</Badge>}
            {tag && <Badge variant="yellow">{tag}</Badge>}
          </div>
          {heat > 0 && (
            <div className="shrink-0 flex gap-0.5 rounded-full bg-charcoal/80 backdrop-blur-md border border-cream/10 px-2 py-1">
              {[1, 2, 3].map((i) => (
                <Flame key={i} className={`size-3 ${i <= heat ? "text-chili fill-chili" : "text-cream/25"}`} />
              ))}
            </div>
          )}
        </div>

        {/* bottom title overlay */}
        <div className="absolute inset-x-0 bottom-0 z-10 p-4 sm:p-5">
          <div className="font-display text-xl sm:text-2xl text-cream leading-tight drop-shadow-[0_2px_8px_rgba(0,0,0,0.7)]">
            {name}
          </div>
        </div>
      </div>

      <div className="relative p-4 sm:p-5 flex-1 flex flex-col">
        <p className="text-xs text-cream/60 leading-relaxed line-clamp-2 min-h-[2.5rem]">{desc}</p>

        <div className="mt-4 flex items-center justify-between gap-2 pt-3 border-t border-dashed border-cream/10">
          <div className="flex items-baseline gap-0.5">
            <span className="font-display text-2xl sm:text-3xl text-yellow leading-none drop-shadow-[0_0_14px_color-mix(in_oklab,var(--yellow)_40%,transparent)]">
              {price.replace(/\s?€/, "")}
            </span>
            <span className="font-mono text-xs text-cream/50 ml-0.5">€</span>
          </div>
          <button
            type="button"
            className="group/btn relative inline-flex items-center gap-1.5 rounded-full bg-yellow text-charcoal px-3.5 py-1.5 text-[10px] uppercase tracking-widest font-bold shadow-[0_6px_18px_-6px_color-mix(in_oklab,var(--yellow)_70%,transparent)] hover:bg-cream active:scale-95 transition-all"
          >
            Notify
            <ArrowUpRight className="size-3 transition-transform group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5" />
          </button>
        </div>
      </div>
    </article>
  );
}



function Badge({ children, variant = "dark" }: { children: React.ReactNode; variant?: "dark" | "yellow" | "chili" }) {
  const styles = {
    dark: "bg-charcoal/70 text-cream backdrop-blur-sm",
    yellow: "bg-yellow text-charcoal",
    chili: "bg-chili text-cream",
  } as const;
  return (
    <span className={`text-[10px] uppercase tracking-widest px-2 py-1 ${styles[variant]}`}>{children}</span>
  );
}

/* ---------- LOOKBOOK ---------- */
function Lookbook() {
  const main = useEntry("shop", "lookbook");
  const tiles = useEntries("shop", "lookbook_tiles");
  const f = useField();
  const fallbacks = [community, heroBirria, quesabirria, streetNight];
  const spans = [
    "col-span-12 md:col-span-8 aspect-[16/10]",
    "col-span-6 md:col-span-4 aspect-square",
    "col-span-6 md:col-span-4 aspect-square",
    "col-span-12 md:col-span-8 aspect-[16/9] md:aspect-[16/8]",
  ];
  return (
    <section className="relative border-y border-border overflow-hidden">
      <div className="mx-auto max-w-7xl px-4 sm:px-5 md:px-8 py-14 md:py-24">
        <SectionHead kicker={f(main, "kicker", "Lookbook 001")} title={f(main, "title", "FOOD. STREET. UNIFORM.")} />
        <div className="mt-8 md:mt-10 grid grid-cols-12 gap-2 sm:gap-3">
          {spans.map((span, i) => {
            const tile = tiles[i];
            return (
              <img
                key={tile?.key ?? i}
                src={tile?.image ?? fallbacks[i]}
                alt={f(tile, "alt", "Tacoya lookbook image")}
                loading="lazy"
                className={`${span} object-cover w-full rounded-lg sm:rounded-none border border-border`}
              />
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ---------- PERKS ---------- */
function Perks() {
  const items = [
    { i: VanFastIcon, t: "EU shipping", d: "From Helsinki. 2–5 days across Nordics & EU." },
    { i: ShieldKeyholeIcon, t: "30-day return", d: "Sealed bottles only. Apparel unworn." },
    { i: BoxAltIcon, t: "Drop guarantee", d: "Numbered batches. No restocks of Drop 001." },
    { i: StarFilledIcon, t: "Built by chefs", d: "Recipes from the actual Tacoya kitchen line." },
  ];
  return (
    <section className="mx-auto max-w-7xl px-4 sm:px-5 md:px-8 py-16">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
        {items.map(({ i: Icon, t, d }, idx) => (
          <div
            key={t}
            className="group relative overflow-hidden rounded-2xl border border-cream/10 bg-gradient-to-br from-card to-charcoal/80 p-5 sm:p-6 transition-all duration-300 hover:border-yellow/40 hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-20px_color-mix(in_oklab,var(--yellow)_40%,transparent)]"
          >
            <div className="pointer-events-none absolute inset-x-6 top-0 h-px bg-gradient-to-r from-transparent via-yellow/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
            <div className="pointer-events-none absolute -top-10 -right-10 size-24 rounded-full bg-yellow/10 blur-2xl opacity-0 group-hover:opacity-100 transition-opacity" />

            <div className="flex items-center justify-between">
              <div className="relative flex items-center justify-center size-10 rounded-xl border border-yellow/25 bg-yellow/10 text-yellow shadow-[inset_0_1px_0_0_rgba(255,255,255,0.08)] group-hover:bg-yellow/15 transition-colors">
                <Icon className="size-[18px]" />
              </div>
              <span className="text-[9px] font-mono uppercase tracking-[0.2em] text-cream/30">0{idx + 1}</span>
            </div>

            <div className="mt-4 font-display text-base sm:text-lg text-cream leading-tight">{t}</div>
            <div className="mt-1.5 text-[11px] sm:text-xs text-cream/55 leading-relaxed">{d}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ---------- FAQ ---------- */
function FAQ() {
  const qs = [
    { q: "When does Drop 001 go live?", a: "Two weeks from today, 18:00 CET. Early-access emails get a 24-hour head start." },
    { q: "Will there be restocks?", a: "No. Drop 001 is a single, numbered run. Future drops are reworked, not reprinted." },
    { q: "Where do you ship?", a: "All EU. Nordics 2–3 days, rest of EU 3–5 days. UK and US coming with Drop 002." },
    { q: "Are the sauces shelf-stable?", a: "Yes. Sealed bottles last 12 months unopened, 3 months chilled after opening." },
  ];
  return (
    <section className="mx-auto max-w-7xl px-4 sm:px-5 md:px-8 py-12 md:py-24 border-t border-border">
      <SectionHead kicker="FAQ" title="THE BASICS" />
      <div className="mt-6 md:mt-10 grid md:grid-cols-2 gap-2.5 md:gap-4">
        {qs.map((x, i) => (
          <details
            key={x.q}
            className="group rounded-xl md:rounded-2xl border border-cream/10 bg-gradient-to-br from-card to-charcoal/70 px-4 py-3.5 md:px-6 md:py-5 open:border-yellow/40 open:shadow-[0_12px_30px_-18px_color-mix(in_oklab,var(--yellow)_50%,transparent)] hover:border-yellow/30 transition-all"
          >
            <summary className="cursor-pointer list-none flex items-center justify-between gap-4">
              <div className="flex items-center gap-3 min-w-0">
                <span className="hidden sm:inline-flex shrink-0 items-center justify-center size-7 rounded-md border border-yellow/25 bg-yellow/10 font-mono text-[10px] text-yellow">
                  0{i + 1}
                </span>
                <span className="font-display text-base md:text-xl text-cream leading-tight">{x.q}</span>
              </div>
              <span className="shrink-0 flex items-center justify-center size-7 md:size-8 rounded-full border border-yellow/30 bg-yellow/10 text-yellow text-lg leading-none group-open:rotate-45 group-open:bg-yellow group-open:text-charcoal transition-all">
                +
              </span>
            </summary>
            <p className="mt-3 md:mt-4 pl-0 sm:pl-10 text-cream/70 text-[13px] md:text-sm leading-relaxed">{x.a}</p>
          </details>
        ))}
      </div>
    </section>
  );
}

/* ---------- NOTIFY ---------- */
function Notify() {
  const notify = useEntry("shop", "notify");
  const f = useField();
  const [email, setEmail] = useState("");
  const [state, setState] = useState<"idle" | "ok" | "err">("idle");
  const [err, setErr] = useState("");

  function submit(e: React.FormEvent) {
    e.preventDefault();
    const r = emailSchema.safeParse(email);
    if (!r.success) { setErr(r.error.issues[0].message); setState("err"); return; }
    setErr(""); setState("ok");
  }


  return (
    <section className="mx-auto max-w-7xl px-4 sm:px-5 md:px-8 pb-24">
      <div className="relative overflow-hidden rounded-2xl sm:rounded-[2rem] border border-yellow/25 bg-gradient-to-br from-card via-charcoal to-charcoal p-6 sm:p-10 md:p-16 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.9),inset_0_1px_0_0_rgba(255,255,255,0.06)]">
        <div className="pointer-events-none absolute -top-24 -right-24 size-80 rounded-full opacity-40 blur-3xl bg-yellow animate-[fab-pulse_4s_ease-in-out_infinite]" />
        <div className="pointer-events-none absolute -bottom-24 -left-24 size-80 rounded-full opacity-25 blur-3xl bg-chili" />
        <div className="pointer-events-none absolute inset-0 opacity-[0.06] [background-image:linear-gradient(rgba(245,235,210,.4)_1px,transparent_1px),linear-gradient(90deg,rgba(245,235,210,.4)_1px,transparent_1px)] [background-size:32px_32px]" />
        <div className="pointer-events-none absolute inset-x-8 top-0 h-px bg-gradient-to-r from-transparent via-yellow/60 to-transparent" />

        {/* fire effects */}
        <div aria-hidden className="pointer-events-none absolute inset-x-0 bottom-0 h-2/3 bg-[radial-gradient(ellipse_at_50%_120%,color-mix(in_oklab,var(--chili)_55%,transparent),transparent_60%)] animate-[fab-flicker_2.6s_ease-in-out_infinite]" />
        <div aria-hidden className="pointer-events-none absolute inset-x-0 bottom-0 h-1/3 bg-[radial-gradient(ellipse_at_50%_120%,color-mix(in_oklab,var(--yellow)_60%,transparent),transparent_65%)] mix-blend-screen animate-[fab-flicker_1.8s_ease-in-out_infinite] [animation-delay:.4s]" />
        <div aria-hidden className="pointer-events-none absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-yellow to-transparent opacity-80" />

        {/* embers */}
        <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
          {[...Array(8)].map((_, i) => (
            <span
              key={i}
              className="absolute bottom-0 size-1 rounded-full bg-yellow/80 shadow-[0_0_8px_2px_color-mix(in_oklab,var(--chili)_70%,transparent)] animate-[ember-rise_var(--d)_ease-in_infinite]"
              style={{
                left: `${(i + 1) * 11}%`,
                ['--d' as string]: `${4 + (i % 4)}s`,
                animationDelay: `${i * 0.6}s`,
              }}
            />
          ))}
        </div>

        <div className="relative grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-yellow/30 bg-yellow/10 px-3 py-1 mb-5">
              <span className="size-1.5 rounded-full bg-yellow animate-pulse" />
              <span className="text-[10px] uppercase tracking-[0.3em] text-yellow font-semibold">{f(notify, "badge", "Early access")}</span>
            </div>
            <h2 className="font-display text-[2.75rem] sm:text-5xl md:text-7xl text-cream leading-[0.9] tracking-tight">
              {f(notify, "title_a", "GET FIRST")} <br />
              <span className="text-yellow drop-shadow-[0_0_24px_color-mix(in_oklab,var(--yellow)_55%,transparent)]">{f(notify, "title_b", "DIBS.")}</span>
            </h2>
            <p className="mt-5 text-cream/75 max-w-md text-base sm:text-lg leading-relaxed">
              {f(notify, "copy", "24-hour head start. Drop alerts. Behind-the-line stories. No spam, ever.")}
            </p>
            <div className="mt-6 hidden md:flex items-center gap-5 text-[10px] uppercase tracking-[0.25em] text-cream/55">
              <span className="flex items-center gap-2"><span className="size-1 rounded-full bg-lime" /> {f(notify, "members", "2.4k members")}</span>
              <span className="h-3 w-px bg-cream/15" />
              <span>{f(notify, "frequency", "Avg 1 email / drop")}</span>
            </div>
          </div>

          <div>
            {state === "ok" ? (
              <div className="rounded-xl border border-yellow/40 bg-yellow/5 p-6 backdrop-blur-sm">
                <div className="font-display text-3xl text-yellow">{f(notify, "success_title", "You're on the list.")}</div>
                <p className="text-cream/70 text-sm mt-2">{f(notify, "success_text", "Check your inbox — we'll ping you 24 hours before public drop.")}</p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-3">
                <div className="group relative rounded-xl border border-cream/10 bg-charcoal/60 p-1.5 backdrop-blur-md focus-within:border-yellow/60 focus-within:shadow-[0_0_0_4px_color-mix(in_oklab,var(--yellow)_15%,transparent)] transition-all flex flex-col sm:flex-row gap-1.5">
                  <label htmlFor="drop-email" className="sr-only">Email address for drop alerts</label>
                  <input
                    id="drop-email"
                    type="email"
                    aria-label="Email address for drop alerts"
                    placeholder={f(notify, "placeholder", "your@email.com")}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    maxLength={255}
                    className="flex-1 bg-transparent px-4 py-3.5 text-cream outline-none placeholder:text-cream/55 text-sm"
                  />
                  <button type="submit" className="group/btn relative overflow-hidden rounded-lg bg-yellow text-charcoal px-6 py-3.5 text-xs uppercase tracking-[0.2em] font-bold whitespace-nowrap hover:bg-cream active:scale-[0.98] transition-all shadow-[0_8px_24px_-8px_color-mix(in_oklab,var(--yellow)_70%,transparent)]">
                    <span className="relative z-10">{f(notify, "submit_label", "Notify me →")}</span>
                    <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/40 to-transparent group-hover/btn:translate-x-full transition-transform duration-700" />
                  </button>
                </div>
                {err && <div className="text-chili text-xs uppercase tracking-widest pl-1">{err}</div>}
                <p className="text-[10px] uppercase tracking-widest text-cream/40 pl-1">
                  {f(notify, "disclaimer", "By signing up you agree to occasional drop emails.")}
                </p>
              </form>
            )}
            <div className="mt-6 flex items-center gap-6 text-[10px] uppercase tracking-widest text-cream/50">
              <Link to="/menu" className="hover:text-yellow transition-colors">Menu →</Link>
              <Link to="/locations" className="hover:text-yellow transition-colors">Locations →</Link>
            </div>

          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- HELPERS ---------- */
function SectionHead({ kicker, title, right }: { kicker: string; title: string; right?: string }) {
  return (
    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-chili mb-3">{kicker}</div>
        <h2 className="font-display text-4xl sm:text-5xl md:text-7xl text-cream leading-none">{title}</h2>
      </div>
      {right && <div className="text-cream/60 text-sm md:text-right max-w-xs">{right}</div>}
    </div>
  );
}
